/**
 * Cálculo PURO de los tramos del mapa de calor (modo único desde 2026-08-04).
 *
 * El COLOR es SEMÁNTICO: cada tramo toma el ESTADO del foco dominante (misma
 * paleta que la leyenda — sin tratar=rojo, previsto=azul, tratado=verde,
 * resuelto=apagado). La presión (severidad × frescura × nº reportes ×
 * distancia gaussiana) solo modula la INTENSIDAD, nunca el tono: el degradado
 * antiguo verde→rojo por presión pintaba VERDE un reporte suelto y se leía
 * como "sano" (QA usuario 2026-08-04).
 *
 * Módulo sin React ni supabase para poder testearlo caso a caso.
 */
import type { Focus, GhRow, LineState } from "./farmHealthModel";

export interface HeatSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Presión total 0..1 (intensidad del tramo). */
  t: number;
  /** Estado del foco dominante — decide el color. */
  state: LineState;
}

const SEV_W: Record<string, number> = { low: 0.35, medium: 0.6, high: 0.85, critical: 1 };
/**
 * El peso de UN parte: su severidad y nada más.
 *
 * Llevaba un factor por número de reportes, que tenía sentido cuando un foco
 * ERA un grupo de partes. Desde que cada parte es su propio foco (31-08-2026)
 * sobra: tres partes en el mismo sitio ya suman tres contribuciones en el
 * bucle de `computeHeatSegments`, que es la misma acumulación pero sin
 * contarla dos veces.
 */
export const focusWeight = (f: Focus) => Math.min(1, SEV_W[f.severity ?? ""] ?? 0.45);

/** Orden de gravedad: en empate de contribución gana el más grave. */
const STATE_PRECEDENCE: LineState[] = ["open", "planned", "treated", "resolved"];
/** Un foco cerrado deja rastro tenue (histórico), no compite con los abiertos. */
const STATE_DAMP: Partial<Record<LineState, number>> = { resolved: 0.3 };

const SIGMA = 8; // metros — alcance del kernel gaussiano de cada foco
const BUCKET_M = 10; // longitud objetivo de cada tramo de línea
const MIN_PRESSURE = 0.04; // por debajo, el tramo no se pinta

export function computeHeatSegments(rows: GhRow[], foci: Focus[]): HeatSegment[] {
  const active = foci.filter((f) => f.state !== "no_data");
  if (active.length === 0) return [];
  const segments: HeatSegment[] = [];
  for (const r of rows) {
    // El kernel NO cruza de sector: un foco solo calienta las líneas de SU
    // bloque. Antes el gaussiano (σ=8, umbral 0.04 → alcance ~17 m) teñía
    // líneas del bloque vecino cuyo tooltip decía "Sin datos" — y un sector
    // coloreado de más es un sector que se trata de más (adversarial
    // 2026-08-04).
    const rowFoci = active.filter((f) => f.blockId === r.blockId);
    if (rowFoci.length === 0) continue;
    const len = Math.max(1, r.lengthM);
    const nBuckets = Math.max(1, Math.round(len / BUCKET_M));
    for (let i = 0; i < nBuckets; i++) {
      const t0 = i / nBuckets;
      const t1 = (i + 1) / nBuckets;
      const mx = r.a.x + ((t0 + t1) / 2) * (r.b.x - r.a.x);
      const my = r.a.y + ((t0 + t1) / 2) * (r.b.y - r.a.y);
      const byState = new Map<LineState, number>();
      let pressure = 0;
      for (const f of rowFoci) {
        const d2 = (f.x - mx) ** 2 + (f.y - my) ** 2;
        const p = focusWeight(f) * (STATE_DAMP[f.state] ?? 1) * Math.exp(-d2 / (2 * SIGMA * SIGMA));
        if (p <= 0) continue;
        pressure += p;
        byState.set(f.state, (byState.get(f.state) ?? 0) + p);
      }
      if (pressure < MIN_PRESSURE) continue;
      let state: LineState = "open";
      let bestP = -1;
      for (const s of STATE_PRECEDENCE) {
        const p = byState.get(s) ?? 0;
        if (p > bestP + 1e-9) {
          bestP = p;
          state = s;
        }
      }
      segments.push({
        x1: r.a.x + t0 * (r.b.x - r.a.x),
        y1: r.a.y + t0 * (r.b.y - r.a.y),
        x2: r.a.x + t1 * (r.b.x - r.a.x),
        y2: r.a.y + t1 * (r.b.y - r.a.y),
        t: Math.min(1, pressure),
        state,
      });
    }
  }
  return segments;
}
